import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ValidationErrorType } from '@shared/models/enums/backend-error-type.enum';
import { ValidationError } from '@shared/models/validation-error';
import { Question } from '@shared/models/views-models/question-view';
import { getNumberOfCharactersInString, utf8ByteCount } from './form.util';

export const getValidators = (
  question: Question,
  enableValidators?: boolean,
): ((control: AbstractControl) => ValidationErrors | null)[] => {
  if (!enableValidators) return [];
  const validators = [];
  if (question.mandatory) {
    validators.push(Validators.required);
  }
  if (question.type === 'OPENANSWER') {
    if (question.formatRegex) {
      validators.push(Validators.pattern(RegExp(question.formatRegex, 'u')));
    }
    if (question.presentation === 'SINGLE_LINE') {
      validators.push(maxLengthValidator(255, 'utf16'));
    } else if (question.presentation === 'MULTI_LINE') {
      validators.push(maxLengthValidator(4000, 'bytes'));
    }
  } else if (question.type === 'MULTIPLE_CHOICE') {
    validators.push(atLeastOneCheckboxCheckedValidator);
  }
  return validators;
};

export function atLeastOneCheckboxCheckedValidator(
  control: AbstractControl,
): ValidationErrors | null {
  const formGroup = control as FormGroup;

  const isAnyCheckboxChecked = Object.values(formGroup.controls).some(
    (control) => control.value === true,
  );

  return isAnyCheckboxChecked ? null : { atLeastOneRequired: true };
}

export function maxLengthValidator(
  maxLength: number,
  typeOfMax: 'characters' | 'bytes' | 'utf16',
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (typeOfMax === 'characters') {
      if (value && getNumberOfCharactersInString(value) > maxLength) {
        return {
          maxLength: {
            requiredLength: maxLength,
            actualLength: getNumberOfCharactersInString(value),
          },
        };
      }
    } else if (typeOfMax === 'bytes') {
      const byteLength = utf8ByteCount(value);
      if (value && byteLength > maxLength) {
        return {
          maxLength: { requiredLength: maxLength, actualLength: byteLength },
        };
      }
    } else if (typeOfMax === 'utf16') {
      if (value && value.length > maxLength) {
        return {
          maxLength: { requiredLength: maxLength, actualLength: value.length },
        };
      }
    }
    return null;
  };
}

export const handleValidationErrors = (
  question: Question | undefined,
  error: ValidationError,
): ValidatorFn | null => {
  if (!question) {
    return null;
  }

  switch (error.errorType) {
    case ValidationErrorType.Mandatory:
      if (question.type === 'MULTIPLE_CHOICE') {
        return atLeastOneCheckboxCheckedValidator;
      }
      return Validators.required;
    case ValidationErrorType.SpecificError:
      if (error.translationKey === 'validation.text.toolong255') {
        return maxLengthValidator(255, 'utf16');
      }
      if (error.translationKey === 'validation.text.toolong4000') {
        return maxLengthValidator(4000, 'bytes');
      }
      return createBackendErrorValidator(
        error.errorType,
        error.translationKey || error.message,
      );
    case ValidationErrorType.FormatError:
      if (question.type === 'OPENANSWER' && question.formatRegex) {
        return Validators.pattern(RegExp(question.formatRegex, 'u'));
      }
      return createBackendErrorValidator(
        error.errorType,
        error.translationKey || error.message,
      );
    case ValidationErrorType.QuotaError:
      return createBackendErrorValidator(
        error.errorType,
        error.message,
        error.alternativeId,
      );
    default:
      return createBackendErrorValidator(
        error.errorType,
        error.translationKey || error.message,
      );
  }
};

function createBackendErrorValidator(
  errorType: string,
  messageOrKey?: string,
  alternativeId?: string,
): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const errorObj: any = {
      message: messageOrKey,
      backendError: true,
    };

    if (alternativeId) {
      errorObj.alternativeId = alternativeId;
    }

    return {
      [errorType]: errorObj,
    };
  };
}
